import { Data, Match, Matches, Player, PlayerPoints, PlayerResult, TeamGameMatch, TeamGameMatchDataDisplay, TeamGameMatchTeamDisplay } from '../../database.schema';
import { DisplayMatchResult, Points, TotalsPointsWithMatchResults } from '../api/hooks/use-fetch-team-totals-by-day';
import { AWAITING_SCORES, MATCH_FORMATS, TOURNAMENT_STATE_ARCHIVED } from '../app/constants';
import { calculatePositions } from './positions';
import { getTeamGameMatchResultSortedOrder } from './team-games-score-calculator';
import { calculateTotals } from './totals-calculator';

const pointsToResultString = (points: number) => {
  switch (points) {
    case 1:
      return 'beat';
    case 0:
      return 'lost to';
    default:
      return 'halved';
  }
};

export const getTeamTotalsByDay = (data: Data, selectedIndex: number) => {
  const courses = data.courses;
  const groupedMatches: Array<TotalsPointsWithMatchResults> = [];
  const matchData = data.tournaments[selectedIndex].ryderCup?.results;
  if (matchData && matchData.length > 0) {
    const team1PlayerIds = data?.tournaments[selectedIndex].ryderCup?.team1.playerIds ?? [];
    const team2PlayerIds = data?.tournaments[selectedIndex].ryderCup?.team2.playerIds ?? [];
    const t1Players = team1PlayerIds.map((playerId: number) => {
      return data.players.find((player: Player) => player.id === playerId) ?? ({} as Player);
    });
    const t2Players = team2PlayerIds.map((playerId: number) => {
      return data.players.find((player: Player) => player.id === playerId) ?? ({} as Player);
    });
    const teamRunningTotalPoints: Points = { team1Points: 0, team2Points: 0 };
    matchData
      .filter((matches: Matches) => matches.matches.every((match: Match) => match.status !== AWAITING_SCORES))
      .forEach((results) => {
        const format = results.format;
        const course = courses.find((course) => course.id === results.courseId);
        const totals = calculateTotals(t1Players, t2Players, results.matches);
        teamRunningTotalPoints.team1Points += totals.team1Points;
        teamRunningTotalPoints.team2Points += totals.team2Points;
        const teamPoints: Points = { team1Points: 0, team2Points: 0 };
        const matchResults = results.matches.map((match: Match): DisplayMatchResult => {
          teamPoints.team1Points = +match.team1.points;
          teamPoints.team2Points = +match.team2.points;
          return {
            team1Players: match.team1.players.map((t1MatchPlayer: PlayerResult) => t1Players.find((t1Player: Player) => t1Player.id === t1MatchPlayer.playerId)?.nickName ?? ''),
            team2Players: match.team2.players.map((t2MatchPlayer: PlayerResult) => t2Players.find((t2Player: Player) => t2Player.id === t2MatchPlayer.playerId)?.nickName ?? ''),
            result: pointsToResultString(match.team1.points),
            margin: match.resultMargin,
            hasScoreCardData: Boolean(match.resultByHole && !match.resultByHole.every((result: string) => result === '-')),
          };
        });
        groupedMatches.push({
          startingHole: results.startingHole,
          courseId: course?.id ?? 0,
          courseName: course?.name ?? '',
          format,
          totals,
          matchResults,
          teamPoints,
          teamRunningTotalPoints: { ...teamRunningTotalPoints },
          isArchivedTournament: data.tournaments[selectedIndex].state === TOURNAMENT_STATE_ARCHIVED,
        });
      });
  }
  return groupedMatches;
};

export const getTeamGamesByMatch = (data: Data, selectedIndex: number): Array<TeamGameMatchDataDisplay> => {
  const matches = data.tournaments[selectedIndex].teamGames?.matches;
  const tierMatchDisplays: Array<TeamGameMatchDataDisplay> = [];
  matches
    ?.filter((match) => match.teams.every((team) => team.scoresEntered))
    ?.forEach((match: TeamGameMatch, matchIndex: number) => {
      const resultMatch =
        match.matchFormat === MATCH_FORMATS.tierPoints_6_6_6.id || match.matchFormat === MATCH_FORMATS.tierPoints_1_2_3.id
          ? { ...getTeamGameMatchResultSortedOrder(match) }
          : { ...match };
      const tierMatchTeams: Array<TeamGameMatchTeamDisplay> = [];
      const tierIndividualStandings: Array<PlayerPoints> = [];
      const courseId = resultMatch.courseId;
      const startingHole = resultMatch.startingHole;
      const matchFormat = resultMatch.matchFormat;
      const courseName = data.courses.find((course) => course.id === courseId)?.name ?? '';
      var hasScoreCard = false;
      resultMatch.teams.forEach((team) => {
        const tierMatch: TeamGameMatchTeamDisplay = { players: [], points: team.points, result: team.result };
        hasScoreCard = team.players.every((player) => player?.round && player.round?.scoreCard?.length === 18) ? true : false;
        team.players.forEach((teamPlayer) => {
          const player = data.players.find((player) => player.id === teamPlayer.playerId);
          if (player) {
            const playerTierDisplay = {
              name: player.name,
              nickName: player.nickName,
              round: { ...teamPlayer.round },
            };
            const playerPoints = {
              player,
              points: teamPlayer.points,
              average: 0,
              won: 0,
              lost: 0,
              halved: 0,
              captainPoints: 0,
              position: 0,
              roundsPlayed: 0,
              nearestThePin: teamPlayer.nearestThePin,
              nearestThePinIn2: teamPlayer.nearestThePinIn2,
              longestDrive: teamPlayer.longestDrive,
            };
            tierMatch.players.push({ ...playerTierDisplay });
            tierIndividualStandings.push({ ...playerPoints });
          }
        });
        tierMatchTeams.push({ ...tierMatch });
      });
      tierMatchDisplays.push({
        courseId,
        courseName,
        startingHole,
        matchFormat,
        teamGameMatchTeams: tierMatchTeams,
        teamGameMatchIndividualStandings: calculatePositions(tierIndividualStandings, 'points'),
        hasScoreCard,
      });
      if ((matchFormat === MATCH_FORMATS.singles.id || matchFormat === MATCH_FORMATS.pairs.id) && match.matchPlayMatches) {
        const matchPlayMatches: Array<DisplayMatchResult> = [];
        match.matchPlayMatches.forEach((matchPlayMatch) => {
          const matchResult: DisplayMatchResult = {
            team1Players: matchPlayMatch.team1.playerIds.map((playerId) => data.players.find((dPlayer) => dPlayer.id === playerId)?.nickName ?? ''),
            team2Players: matchPlayMatch.team2.playerIds.map((playerId) => data.players.find((dPlayer) => dPlayer.id === playerId)?.nickName ?? ''),
            result: pointsToResultString(matchPlayMatch.team1.points),
            margin: matchPlayMatch.resultMargin,
            hasScoreCardData: true,
          };
          matchPlayMatches.push(matchResult);
        });
        tierMatchDisplays[matchIndex].matchPlayMatches = [...matchPlayMatches];
      }
    });
  return tierMatchDisplays;
};
